import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { FiX, FiArrowLeft, FiMail, FiEyeOff } from 'react-icons/fi';
import moment from 'moment';
import ReactHtmlParser from 'react-html-parser';
import httpService from 'services/httpService.js';
import { store } from 'Store';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import CustomCKEditor from 'Components/CustomCKEditor';
import { createAnnouncementAsync, getAnnouncementPreviewInfo } from '../services/announcementService';
import styles from '../AnnouncementsV2.module.css';

const STEP_EDIT = 'edit';
const STEP_PREVIEW = 'preview';

const BLOCKED_REASON_TEXT = {
  notifications_disabled:
    'Email notifications are off for this course. This will post to the course page only.',
  plan_not_eligible:
    'This course plan does not include announcement emails. This will post to the course page only.',
  no_active_students:
    'No students are actively enrolled yet. This will post to the course page only.',
};

const CreateAnnouncementModal = ({
  show,
  onHide,
  onSuccess,
  editAnnouncement,
  courseId
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [step, setStep] = useState(STEP_EDIT);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [loadingPreviewInfo, setLoadingPreviewInfo] = useState(false);
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  const isEditMode = !!editAnnouncement;

  // Reset form when modal opens/closes or edit announcement changes
  useEffect(() => {
    if (show) {
      if (editAnnouncement) {
        setTitle(editAnnouncement.title || '');
        setContent(editAnnouncement.description || '');
      } else {
        setTitle('');
        setContent('');
      }
      setEditingContent(false);
      setEditorReady(false);
      setStep(STEP_EDIT);
      setPreviewInfo(null);
      setShowAllRecipients(false);
    }
  }, [show, editAnnouncement]);

  // Editing an announcement never re-sends email, so only creation needs this
  useEffect(() => {
    let cancelled = false;

    if (show && !isEditMode && courseId) {
      setLoadingPreviewInfo(true);
      getAnnouncementPreviewInfo(courseId)
        .then((info) => {
          if (!cancelled) {
            setPreviewInfo(info);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPreviewInfo(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoadingPreviewInfo(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [show, isEditMode, courseId]);

  const handleStartEditContent = () => {
    setEditingContent(true);
    setEditorReady(false);
  };

  const handleContentBoxKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleStartEditContent();
    }
  };

  // Get user from store
  const getUser = () => {
    const state = store.getState();
    return state.userInfo;
  };

  const handleGoToPreview = (e) => {
    e.preventDefault();

    if (!title.trim()) {
      displayGlobalMessage('Please enter an announcement title', 'error');
      return;
    }

    if (!content.trim()) {
      displayGlobalMessage('Please enter announcement content', 'error');
      return;
    }

    setEditingContent(false);
    setShowAllRecipients(false);
    setStep(STEP_PREVIEW);
  };

  const handleBackToEdit = () => {
    setStep(STEP_EDIT);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    const user = getUser();
    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('description', content);

    try {
      if (isEditMode) {
        // Update existing announcement (use old endpoint)
        const url = `/announcements/api/announcement/${editAnnouncement.id}/edit_announcement/?format=json`;
        await httpService.instance({
          method: 'patch',
          url: url,
          data: formData,
          headers: {
            Authorization: `Token ${user.token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        displayGlobalMessage('Announcement updated successfully', 'success');
        onSuccess();
      } else {
        // Create new announcement using async V2 endpoint
        // Email notification is controlled by course settings
        const response = await createAnnouncementAsync(courseId, {
          title: title.trim(),
          description: content
        });

        if (response.email_task_id) {
          // Email sending started
          displayGlobalMessage(
            `Announcement created! Sending emails to ${response.total_recipients} students...`,
            'success'
          );
          // Pass the task info to parent for progress tracking
          onSuccess({ emailTaskId: response.email_task_id, totalRecipients: response.total_recipients });
        } else if (response.email_warning) {
          // Announcement created but email not available
          displayGlobalMessage(`Announcement created. ${response.email_warning}`, 'warning');
          onSuccess();
        } else {
          // Announcement created without email
          displayGlobalMessage('Announcement created successfully', 'success');
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      displayGlobalMessage(
        isEditMode ? 'Failed to update announcement' : 'Failed to create announcement',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContentChange = (data) => {
    setContent(data.html || data.text || '');
  };

  const renderRecipientBanner = () => {
    if (isEditMode) {
      return null;
    }

    if (loadingPreviewInfo) {
      return (
        <div className={styles.previewBannerMuted}>
          <Spinner animation="border" size="sm" />
          <span>Checking who will be notified...</span>
        </div>
      );
    }

    if (!previewInfo) {
      return (
        <div className={styles.previewBannerMuted}>
          <FiEyeOff size={16} />
          <span>
            Could not check email notification settings. Publishing may still email students.
          </span>
        </div>
      );
    }

    if (!previewInfo.will_send_email) {
      return (
        <div className={styles.previewBannerMuted}>
          <FiEyeOff size={16} />
          <span>
            {BLOCKED_REASON_TEXT[previewInfo.blocked_reason]
              || 'This will post to the course page only.'}
          </span>
        </div>
      );
    }

    const sampleRecipients = previewInfo.sample_recipients || [];
    const listedRecipients = previewInfo.recipients || sampleRecipients;
    const hiddenRecipientCount = previewInfo.total_recipients - sampleRecipients.length;

    return (
      <div className={styles.previewBannerAlert}>
        <FiMail size={16} />
        <div className={styles.previewBannerContent}>
          <strong>
            {`Publishing will email ${previewInfo.total_recipients} `}
            {previewInfo.total_recipients === 1 ? 'student' : 'students'}
          </strong>
          {sampleRecipients.length > 0 && !showAllRecipients && (
            <div className={styles.previewRecipientList}>
              {sampleRecipients.join(', ')}
              {hiddenRecipientCount > 0 && (
                <>
                  {' and '}
                  <button
                    type="button"
                    className={styles.previewMoreButton}
                    onClick={() => setShowAllRecipients(true)}
                    disabled={listedRecipients.length <= sampleRecipients.length}
                  >
                    {`${hiddenRecipientCount} more`}
                  </button>
                </>
              )}
            </div>
          )}
          {showAllRecipients && (
            <>
              <div className={styles.previewRecipientChips}>
                {listedRecipients.map((email, index) => (
                  <span key={`${email}-${index}`} className={styles.previewRecipientChip}>
                    {email}
                  </span>
                ))}
              </div>
              <div className={styles.previewRecipientListFooter}>
                <span>
                  {listedRecipients.length < previewInfo.total_recipients
                    ? `Showing the first ${listedRecipients.length} of ${previewInfo.total_recipients}`
                    : `Showing all ${listedRecipients.length}`}
                </span>
                <button
                  type="button"
                  className={styles.previewMoreButton}
                  onClick={() => setShowAllRecipients(false)}
                >
                  Show less
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderEditStep = () => (
    <form onSubmit={handleGoToPreview}>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Announcement Title</label>
        <input
          type="text"
          className={styles.formInput}
          placeholder="Enter announcement title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Content (Click to edit)</label>
        {editingContent ? (
          <div className={styles.editorContainer}>
            <CustomCKEditor
              name={`AnnouncementV2-${isEditMode ? editAnnouncement?.id : 'new'}`}
              placeholder="Write your announcement..."
              value={content}
              onChange={handleContentChange}
              config={{ resize_enabled: false }}
              onReadyChange={setEditorReady}
            />
            {editorReady && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setEditingContent(false)}
                className={styles.editDoneBtn}
              >
                Done
              </Button>
            )}
          </div>
        ) : (
          <div
            onClick={handleStartEditContent}
            onKeyDown={handleContentBoxKeyDown}
            role="button"
            tabIndex={0}
            className={`${styles.clickToEditBox} ${!content ? styles.clickToEditBoxEmpty : ''}`}
            dangerouslySetInnerHTML={{
              __html: content || '<span style="color: #9ca3af; font-style: italic;">Click to write your announcement...</span>'
            }}
          />
        )}
      </div>
    </form>
  );

  const renderPreviewStep = () => (
    <div className={styles.previewStep}>
      {renderRecipientBanner()}

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>How students will see this</label>
        <div className={`card card-body announcement-content ${styles.previewCard}`}>
          <b style={{ display: 'inline-block' }}>
            {moment(isEditMode ? editAnnouncement.publish_date : undefined)
              .format('MMM DD, YYYY HH:mm A')}
            :{' '}
          </b>
          {title.trim() && <div className={styles.previewTitle}>{title.trim()}</div>}
          {ReactHtmlParser(content)}
        </div>
      </div>
    </div>
  );

  const isPreviewStep = step === STEP_PREVIEW;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      enforceFocus={false}
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          {isPreviewStep
            ? 'Preview Announcement'
            : (isEditMode ? 'Edit Announcement' : 'Create New Announcement')}
        </Modal.Title>
        <button className={styles.modalCloseButton} onClick={onHide}>
          <FiX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {isPreviewStep ? renderPreviewStep() : renderEditStep()}
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        {isPreviewStep ? (
          <>
            <button
              type="button"
              className={styles.backButton}
              onClick={handleBackToEdit}
              disabled={isSubmitting}
            >
              <FiArrowLeft size={16} />
              Back to edit
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting && <Spinner animation="border" size="sm" />}
              {isEditMode ? 'Update Announcement' : 'Publish Announcement'}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onHide}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleGoToPreview}
            >
              Preview
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateAnnouncementModal;
